import { type ReactNode, useState } from 'react'
import MessagingInteractionIllustration from '@/components/SpotIllustration/MessagingInteractionIllustration'
import { Button, RawButton } from '@/components/Button'
import { Icon } from '@/components/Icon'
import { IconButton } from '@/components/IconButton'
import { isRefreshDesign } from '@/infra/design'
import { Form, FormGroup } from '@/components/Form'
import { ModalSheet } from '@/components/ModalSheet'
import styles from './IncompleteConsultationCompletionDialog.module.css'
import WorkspaceIntercomIllustration from '@/components/SpotIllustration/WorkspaceIntercomIllustration'
import { type ConsultationIncompleteReason } from '@/features/jobs/consultations/Consultation'

export interface IncompleteConsultationCompletionDialogProps {
  showing: boolean
  onClose: () => unknown
  reason?: ConsultationIncompleteReason
  onReasonChange: (reason: ConsultationIncompleteReason) => unknown
  otherText: string
  onOtherTextChange: (otherText: string) => unknown
  isValid: boolean
  onSubmit: () => unknown
}

export function IncompleteConsultationCompletionDialog({
  showing,
  onClose,
  reason,
  onReasonChange,
  otherText,
  onOtherTextChange,
  isValid,
  onSubmit,
}: IncompleteConsultationCompletionDialogProps) {
  return (
    <ModalSheet
      showing={showing}
      disableCloseOnEsc
      disableCloseOnOutsideClick
      onRequestClose={onClose}
    >
      <div
        className={styles.modalContainer}
        data-test="incomplete-consultation-dialog"
      >
        <div className={styles.header}>
          <IconButton
            name="close"
            label="Close"
            className={styles.close}
            onPress={onClose}
            size={24}
          />
          <div className={styles.image}>
            {isRefreshDesign ? (
              <WorkspaceIntercomIllustration className={styles.illustration} />
            ) : (
              <MessagingInteractionIllustration />
            )}
          </div>
        </div>
        <div className={styles.content}>
          <div className={styles.text}>
            <h1 className={styles.title}>
              Before finishing,
              <br />
              tell us why the call wasn't needed.
            </h1>
          </div>
          <div className={styles.radioContainer}>
            <Form id="incomplete-consultation-reason">
              <FormGroup>
                <RadioButtonGroup>
                  <RadioButtonItem
                    active={reason === 'ConsultationNotNeeded'}
                    testId="incomplete-consultation-dialog-reason-not-needed"
                    label="Consultation not needed"
                    onPress={() => onReasonChange('ConsultationNotNeeded')}
                  />
                  <RadioButtonItem
                    active={reason === 'ClientMissedCall'}
                    testId="incomplete-consultation-dialog-reason-client-missed-call"
                    label="Customer missed the call"
                    onPress={() => onReasonChange('ClientMissedCall')}
                  />
                  <RadioButtonItem
                    active={reason === 'ProMissedCall'}
                    testId="incomplete-consultation-dialog-reason-pro-missed-call"
                    label="I missed the call"
                    onPress={() => onReasonChange('ProMissedCall')}
                  />
                  <RadioButtonItem
                    active={reason === 'PreviousProCompletedCall'}
                    testId="incomplete-consultation-dialog-reason-previous-pro-completed"
                    label="Previous Pro completed the call"
                    onPress={() => onReasonChange('PreviousProCompletedCall')}
                  />
                  <RadioButtonItem
                    active={reason === 'ConsultationHandledViaChat'}
                    testId="incomplete-consultation-dialog-reason-handled-via-chat"
                    label="Consultation done via chat"
                    onPress={() => onReasonChange('ConsultationHandledViaChat')}
                  />
                  <RadioButtonItem
                    active={reason === 'Other'}
                    testId="incomplete-consultation-dialog-reason-other"
                    label="Other"
                    onPress={() => onReasonChange('Other')}
                  >
                    {reason === 'Other' && (
                      <textarea
                        className={styles.otherTextArea}
                        data-test="incomplete-consultation-dialog-other-textarea"
                        onChange={(e) => onOtherTextChange(e.target.value)}
                        rows={4}
                        value={otherText}
                      />
                    )}
                  </RadioButtonItem>
                </RadioButtonGroup>
              </FormGroup>
            </Form>
          </div>
        </div>
        <div className={styles.footer}>
          <div className={styles.buttonContainer}>
            <Button
              label="Close"
              onPress={onClose}
              variant="secondary"
              testId="incomplete-consultation-dialog-close-button"
            />
            <Button
              label="Submit & Complete Job"
              isDisabled={!isValid}
              onPress={onSubmit}
              variant="primary"
              testId="incomplete-consultation-dialog-submit-button"
            />
          </div>
          <div className={styles.infoText}>
            <Icon color="var(--grey600)" name="info" size={20} />
            <p className={styles.description}>
              Marking this job as complete will close communication with client.
            </p>
          </div>
        </div>
      </div>
    </ModalSheet>
  )
}

interface RadioButtonGroupProps {
  children: ReactNode
}

function RadioButtonGroup({ children }: RadioButtonGroupProps) {
  return <div className={styles.radioButtonGroup}>{children}</div>
}

interface RadioButtonItemProps {
  active: boolean
  children?: ReactNode
  testId?: string
  label: string
  onPress: () => unknown
}

function RadioButtonItem({
  active,
  children,
  testId,
  label,
  onPress,
}: RadioButtonItemProps) {
  const [hovering, setHovering] = useState(false)

  return (
    <div className={styles.radioButtonItem}>
      <RawButton
        className={styles.radioButtonItemToggle}
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        onPress={onPress}
        testId={testId}
      >
        <Icon
          color={active ? 'var(--theme-base)' : 'var(--grey300)'}
          name={active || hovering ? 'radio-selected' : 'radio-blank'}
          size={24}
        />
        <span className={styles.radioButtonLabel}>{label}</span>
      </RawButton>
      {children && (
        <div className={styles.radioButtonItemContent}>{children}</div>
      )}
    </div>
  )
}
